import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  ComplianceProgramState,
  GetComplianceProgramsQuery,
  GetComplianceProgramsQueryVariables,
} from "types/graphql-schema";

import { currentOrganizationVar } from "@cache/cache";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import Loading from "@components/loading/loading";
import useDebounce from "@components/use-debounce/use-debounce";
import { assertEdgesNonNull, getUrlParam } from "@helpers/helpers";

import getComplianceProgramsQuery from "../graphql/get-compliance-programs-query";
import { useProgramsSection } from "../programs-section-context";
import ComplianceProgramProgress from "./compliance-program-progress";

/**
 * The "Progress" tab for the Reviews/Surveys pages: the same per-program
 * progress view shown inside a program, plus a program picker so the user can
 * switch between programs. The picker uses the same default-selection logic as
 * the Reporting page and is filtered to the section's assessment types (only
 * reviews in the Reviews section, only surveys in the Surveys section).
 */
const ProgramProgressPage = () => {
  const { assessmentTypes, noun, nounPlural } = useProgramsSection();
  const location = useLocation();
  const history = useHistory();
  const organization = currentOrganizationVar();
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 500);
  const programIdFromUrl = getUrlParam(location, "programId");
  const [selectedProgramId, setSelectedProgramId] = useState<number | null>(
    programIdFromUrl ? parseInt(programIdFromUrl) : null,
  );
  const [availablePrograms, setAvailablePrograms] = useState<
    ComboboxGenericOption<number>[] | null
  >(null);

  const handleChangeProgram = useCallback(
    (programId: number, overrideUrl = false) => {
      const params = new URLSearchParams(location.search);
      params.set("programId", `${programId}`);
      const url = `${location.pathname}?${params}`;
      if (overrideUrl) {
        history.replace(url);
      } else {
        history.push(url);
      }
    },
    [history, location],
  );

  const { loading: isLoadingProgramList } = useQuery<
    GetComplianceProgramsQuery,
    GetComplianceProgramsQueryVariables
  >(getComplianceProgramsQuery, {
    variables: {
      search: debouncedQuery,
      organizationId: organization.id,
      state_In: [
        ComplianceProgramState.Published,
        ComplianceProgramState.Paused,
        // A closed cycle still has results to report on; only writing is locked.
        ComplianceProgramState.Closed,
      ],
      assessmentTypes,
    },
    onCompleted: (response) => {
      const programs = response.detailedCompliancePrograms
        ? assertEdgesNonNull(response.detailedCompliancePrograms)
        : [];
      const programOptions = programs.map(
        ({ id, title, startDate, dueDate }) => ({
          value: id,
          label: title,
          description: `${moment(startDate).format("MMM D, YYYY")} - ${moment(
            dueDate,
          ).format("MMM D, YYYY")}`,
        }),
      );
      setAvailablePrograms((prev) =>
        uniqBy([...programOptions, ...(prev ?? [])], "value"),
      );
      if (!selectedProgramId && programs.length > 0) {
        handleChangeProgram(programs[0].id, true);
      }
    },
  });

  useEffect(() => {
    const idFromUrl = getUrlParam(location, "programId");
    setSelectedProgramId(idFromUrl ? parseInt(idFromUrl) : null);
  }, [location]);

  return (
    <main className="absolute inset-0 flex flex-col overflow-hidden">
      <div className="@container/layout-main mx-auto flex w-full flex-1 flex-col min-h-0">
        <div className="px-8 pt-6 pb-2">
          <div className="text-lg font-semibold text-gray-900">Progress</div>
        </div>
        <div className="flex flex-1 min-h-0 flex-col overflow-auto px-8 pt-6 pb-12">
          {isLoadingProgramList && availablePrograms === null ? (
            <div className="flex justify-center p-10">
              <Loading>Loading...</Loading>
            </div>
          ) : availablePrograms !== null && availablePrograms.length === 0 ? (
            <div className="mt-4 text-gray-500">
              No {nounPlural.toLowerCase()} to display.
            </div>
          ) : (
            selectedProgramId && (
              <ComplianceProgramProgress
                complianceProgramId={selectedProgramId}
                filterRowLeftContent={
                  <div className="w-72">
                    <ComboboxGeneric
                      prefix={noun}
                      options={availablePrograms ?? []}
                      onChangeValue={(opt) => handleChangeProgram(opt.value)}
                      value={
                        availablePrograms?.find(
                          ({ value }) => value === selectedProgramId,
                        ) ?? null
                      }
                      query={query}
                      setQuery={setQuery}
                    />
                  </div>
                }
              />
            )
          )}
        </div>
      </div>
    </main>
  );
};

export default ProgramProgressPage;
